import React, { useState, useEffect, memo } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    Grid,
    Paper,
    Typography,
    TextField,
    Dialog, DialogActions, DialogTitle, DialogContent, Stack,
    CardHeader,
    Chip
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useAuth, useNavigation, useLoading, useSnackbar } from "../../Context/Contexts";
import InventoryIcon from "@mui/icons-material/Inventory";
import { InventoryJournalHeaderViewModel } from "../../Types/ExportTypes";
import CenteredListCard from "../../components/List/CenteredListCardLayout";
import ListCardHeader from "../../components/List/ListCardHeader";
import { LocationAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import useCachedListQuery from "../../hooks/useCachedListQuery";
import { useTranslation } from "react-i18next";
import { buildQueryFilter } from "../../Utils/QueryFilterUtils";
import LoadingSection from "../../components/LoadingSection";
import ResponsivePagination from "../../components/ResponsivePagination";

const InventoryJournalList: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
    const { open = false } = params || {};
    const [documents, setDocuments] = useState<InventoryJournalHeaderViewModel[]>([]);
    const [totalDocuments, setTotalDocuments] = useState(0);
    const [query, setQuery] = useCachedListQuery({
        page: 0,
        pageSize: 10,
        filter: "",
        filters: [],
    }, `inventoryJournalList_${open}`);
    const [filter, setFilter] = useState<string>("");
    const [filterProperty, setFilterProperty] = useState<string>("");
    const [loading, setLoading] = useState(false);

    const { authService } = useAuth();
    const { navigate } = useNavigation();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const [isCreationModalOpen, setIsCreationModalOpen] = useState(false);
    const { t } = useTranslation();


    const fetchDocuments = async () => {
        setLoading(true);
        try {
            const response = await authService.post<{ items: InventoryJournalHeaderViewModel[]; count: number }>(
                `/inventoryApi/GetInventoryJournalList?open=${open}`,
                query
            );
            setDocuments(response.items);
            setTotalDocuments(response.count);
        } catch (error) {
            console.error("Failed to fetch documents:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDocuments();
    }, [query, open]);

    useEffect(() => {
        if (loading) showLoading();
        else hideLoading();
    }, [loading, showLoading, hideLoading]);

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();

        var localFilterProperty = filterProperty;
        //if (!filter) return;
        if (!localFilterProperty?.trim() && filter?.trim()) {
            localFilterProperty = "Default";
        }

        if (localFilterProperty?.trim() && filter?.trim()) {
            const newFilter = buildQueryFilter(localFilterProperty, filter);
            const isDuplicate = query.filters?.some(f =>
                f.property === newFilter.property && f.value === newFilter.value
            );
            if (isDuplicate) return;

            const newFilters = [...(query.filters ?? []), newFilter];
            setQuery({ ...query, filters: newFilters, page: 0, filter: "" });
        } else {
            // No filterProperty: just update the query with the raw filter string
            setQuery({ ...query, page: 0, filter });
        }

        setFilter("");
        setFilterProperty("");
    };

    const handlePageChange = (newPage: number) => {
        setQuery({ ...query, page: newPage });
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setQuery({ ...query, pageSize: newPageSize, page: 0 });
    };

    const handleSelectDocument = (document: InventoryJournalHeaderViewModel) => {
        if (open) {
            navigate({ path: "/inventory/journal/edit", params: { documentNo: document.no, documentType: document.documentType, open } });
        }
        else navigate({ path: "/inventory/journalhistory/edit", params: { documentNo: document.no, documentType: document.documentType, open } });
    };



    const selectCreationType = async () => {
        setIsCreationModalOpen(true);
    };


    const handleCreate = async (documentType: number) => {
        setLoading(true);

        try {
            const response = await authService.post<InventoryJournalHeaderViewModel>(
                `/inventoryApi/CreateDocument?documentType=${documentType}`,
                {}
            );
            setLoading(false);
            navigate({ path: "/inventory/journal/edit", params: { documentNo: response.no, documentType: response.documentType, open: true } });

        } catch (error: any) {
            showSnackbar(`${t("dialog.createJournalError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            setLoading(false);
        }
    };

    type creationInputModal = {
        open: boolean;
        onClose: () => void;
        handleCreate: (documentType: number) => void;
    };
    const CreationModeModal = memo(({ open, onClose, handleCreate }: creationInputModal) => {
        return (
            <Dialog
                open={open}
                onClose={onClose}
                PaperProps={{
                    sx: {
                        width: "80vw",
                        maxWidth: "none",
                        p: 3 // optional padding around the whole dialog
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: "center" }}>
                    {t("common.selectJournalType")}
                </DialogTitle>
                <DialogContent>
                    <Stack spacing={3} direction="column" alignItems="center" mt={2}>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{ minWidth: 200 }}
                            onClick={() => handleCreate(4)}
                            color="primary"
                        >
                            {t("common.stockTaking")}
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{ minWidth: 200 }}
                            onClick={() => handleCreate(0)}
                            color="primary"
                        >
                            {t("common.adjustment")}
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{ minWidth: 200 }}
                            onClick={() => handleCreate(3)}
                            color="primary"
                        >
                            {t("common.receival")}
                        </Button>
                        <Button
                            variant="contained"
                            fullWidth
                            size="large"
                            sx={{ minWidth: 200 }}
                            onClick={() => handleCreate(2)}
                            color="primary"
                        >
                            {t("common.transfer")}
                        </Button>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose}>{t("common.cancel")}</Button>
                </DialogActions>
            </Dialog>

        );
    });

    const getColorForType = (type?: number | null): string => {
        switch (type) {
            case 4:
                return "text.secondary";
            case 0:
                return "secondary.contrastText";
            case 3:
                return "text.primary";
            default:
                return "text.primary";
        }
    };
    return (
        <CenteredListCard title={t("common.invAdjustment")}>

            <CreationModeModal open={isCreationModalOpen} onClose={() => setIsCreationModalOpen(false)} handleCreate={handleCreate} />
            {open == "true" && (
                <Box sx={{ marginBottom: 2 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={selectCreationType}
                    >
                        {t("common.create")}
                    </Button>
                </Box>
            )}
            {/* Search Field */}
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{
                    display: "flex",
                    gap: 2,
                    marginBottom: 2,
                }}
            >
                <Grid container spacing={2}>
                    <Grid item xs={5} lg={2}>
                        <TextField
                            select
                            label={t("common.field")}
                            value={filterProperty}
                            onChange={(e) => setFilterProperty(e.target.value)}
                            SelectProps={{ native: true }}
                            size="small"
                            InputLabelProps={{ shrink: true }}
                        >
                            <option value="">{t("common.default")}</option>
                            <option value="ext_No">{t("common.number")}</option>
                            <option value="ext_LocationCode">{t("common.location")}</option>
                            {open == "false" && (
                                <>
                                    <option value="ext_ApplyGTE">{t("common.inventoryDateGTE")}</option>
                                    <option value="ext_ApplyLTE">{t("common.inventoryDateLTE")}</option>
                                </>
                            )}
                        </TextField>
                    </Grid>
                    <Grid item xs={7} lg={7}>



                        {(() => {
                            switch (filterProperty) {
                                case "ext_LocationCode":
                                    return (
                                        <LocationAutoComplete onChange={(o) => setFilter(o?.value || "")} />
                                    );
                                case "ext_ApplyLTE":
                                    return (
                                        <TextField
                                            label={t("common.dateTo")}
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            onChange={(e) => setFilter(e.target.value || "")}
                                        />
                                    );
                                case "ext_ApplyGTE":
                                    return (
                                        <TextField
                                            label={t("common.dateFrom")}
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            fullWidth
                                            onChange={(e) => setFilter(e.target.value || "")}
                                        />

                                    );
                                default:
                                    return (
                                        <TextField
                                            label={t("common.search")}
                                            variant="outlined"
                                            fullWidth
                                            onChange={(event) => setFilter(event.target.value)}
                                            value={filter}
                                            disabled={loading}
                                        />
                                    );
                            }
                        })()}
                    </Grid>
                    <Grid item xs={12} lg={3}>
                        <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                        >
                            {t("common.search")}
                        </Button>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {query.filters?.map((f, index) => (
                    <Chip
                        key={index}
                        label={`${f.property === "Default" ? t("common.default") : f.property}: ${f.value}`}
                        onDelete={() => {
                            const newFilters = query.filters?.filter((_, i) => i !== index);
                            setQuery({ ...query, filters: newFilters, page: 0 });
                        }}
                        deleteIcon={<DeleteIcon />}
                    />
                ))}
            </Box>

            {/* Card Layout for Orders */}
            <LoadingSection loading={loading}>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    {documents.map((document) => (
                        <Grid item
                            xs={12}
                            sm={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 6}
                            md={documents.length === 1 ? 12 : documents.length === 2 ? 6 : 4}
                            key={document.no}>
                            <Card
                                onClick={() => handleSelectDocument(document)}
                                sx={{
                                    cursor: "pointer",
                                    transition: "box-shadow 0.2s ease-in-out",
                                    "&:hover": {
                                        boxShadow: 4,
                                        backgroundColor: "secondary.main",
                                    },
                                }}
                            >
                                <CardHeader
                                    title={t(`common.journalTypeDescriptions.${document.documentTypeDesc}`)}
                                    sx={{
                                        backgroundColor: getColorForType(document.documentType),
                                        color: "white",
                                        textAlign: "center",
                                        "& .MuiCardHeader-title": {
                                            fontWeight: "bold",
                                        },
                                    }}
                                />
                                <CardContent>
                                    <ListCardHeader
                                        icon={<InventoryIcon fontSize="small" color="action" />}
                                        title={document.no}
                                    />
                                    <Typography variant="body2" color="textSecondary">
                                        {t("common.location")}: {document.locationName}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        {t("common.type")}: {t(`common.journalTypeDescriptions.${document.documentTypeDesc}`)}
                                    </Typography>
                                    {open == "false" && (
                                        <Typography variant="body2" color="textSecondary">
                                            {t("common.inventoryDate")}: {document.applyDate ? new Date(document.applyDate).toLocaleString() : ""}
                                        </Typography>

                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {documents.length === 0 && (
                        <Grid item xs={12}>
                            <Typography align="center">${t("common.noEntriesFound")}</Typography>
                        </Grid>
                    )}
                </Grid>
            </LoadingSection>

            {/* Pagination */}
            <ResponsivePagination

                page={query.page}

                pageSize={query.pageSize}

                totalCount={totalDocuments}

                onPageChange={handlePageChange}

            />

        </CenteredListCard>
    );
};

export default InventoryJournalList;